import React, { useState } from "react";

import { useTranslation } from "utils";

import { Button, Flex, Icon, Popover, Typography } from "components";

import { useFieldViewModeAnnouncementSeen } from "pages/app/all-field-view/hooks/useFieldViewModeAnnouncementSeen";

type FieldViewModeAnnouncementProps = {
  /** The view switcher the announcement points at. */
  children: React.ReactNode;
  /** False once the user is already looking at one of the views being announced. */
  isEligible: boolean;
  onTryItOut: () => void;
};

/**
 * One-off pointer at the view switcher, introducing the table and map views to farmers who have only ever
 * seen the timeline. It closes for good as soon as the user dismisses it or follows the call to action, and
 * because the flag is stored per user it stays closed across every farm they own.
 */
export const FieldViewModeAnnouncement = ({ children, isEligible, onTryItOut }: FieldViewModeAnnouncementProps) => {
  const { t } = useTranslation("allFieldView");
  const { hasSeenAnnouncement, isLoadingAnnouncementSeen, markAnnouncementSeen } = useFieldViewModeAnnouncementSeen();

  // the popover has to close immediately; waiting for the setting to save would leave it hanging around
  const [isDismissedInSession, setIsDismissedInSession] = useState(false);

  const isVisible = isEligible && !isLoadingAnnouncementSeen && !hasSeenAnnouncement && !isDismissedInSession;

  const dismiss = () => {
    setIsDismissedInSession(true);
    markAnnouncementSeen();
  };

  const handleTryItOut = () => {
    dismiss();
    onTryItOut();
  };

  const announcement = (
    <Flex vertical gap="0.75rem" className="field-view-mode-announcement-content">
      <Flex justify="space-between" align="flex-start" gap="1rem">
        <Typography variant="paragraph-m" bold className="field-view-mode-announcement-title margin-0">
          {t("viewSwitcher.announcement.title", { defaultValue: "New: Table and Map view" })}
        </Typography>
        <button
          type="button"
          className="field-view-mode-announcement-close"
          onClick={dismiss}
          aria-label={t("viewSwitcher.announcement.dismiss", { defaultValue: "Close" })}
          data-testid="field-view-mode-announcement-close"
        >
          <Icon iconName="tabler/x" size="s" />
        </button>
      </Flex>
      <Typography variant="paragraph-s" className="field-view-mode-announcement-description margin-0">
        {t("viewSwitcher.announcement.description", {
          defaultValue: "See all your fields at a glance, spot where information is missing and enter data faster.",
        })}
      </Typography>
      <Flex justify="flex-end" className="field-view-mode-announcement-actions">
        <Button
          type="secondary"
          size="small"
          onClick={handleTryItOut}
          dataTestId="field-view-mode-announcement-cta"
          icon={<Icon iconName="tabler/arrow-narrow-right" size="s" />}
        >
          {t("viewSwitcher.announcement.cta", { defaultValue: "Try it out" })}
        </Button>
      </Flex>
    </Flex>
  );

  return (
    <Popover
      className="field-view-mode-announcement"
      dataTestId="field-view-mode-announcement"
      visible={isVisible}
      // centred under the switcher so the arrow points straight at it
      placement="bottom"
      // controlled purely by the dismissal state: hovering or clicking the switcher must not reopen it
      trigger={[]}
      content={announcement}
    >
      {children}
    </Popover>
  );
};
